/**
 * PromiseHandler — shared invoke utility for Rust IPC
 *
 * Manages promise creation and completion for request/response commands
 * sent to the Rust backend via window.__SONGBIRD__. Both native (Tauri)
 * and WebSocket modes use the same interface:
 *
 *   JS → Rust:  backend.emitEvent('__songbird__invoke', { name, params, resultId })
 *   Rust → JS:  backend.emitByBackend('__songbird__complete', { promiseId, result })
 *
 * This module is the single source of truth for the invoke path.
 * Transports expose invoke() on the Transport interface; channel handlers
 * receive a `dispatch` function that delegates here.
 */

// Ensure the Backend class is bootstrapped on window.__SONGBIRD__
import '@/lib/native/check_native_interop.js';

/**
 * Parse `value` if it looks like a JSON string (`{...}` or `[...]`),
 * otherwise pass through unchanged. Used to normalize IPC replies
 * across transports — Tauri stringifies, WS doesn't. Strings that
 * aren't valid JSON (e.g. plain text returns) round-trip unchanged.
 */
function maybeParseJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (trimmed.length === 0) return value;
  const first = trimmed[0];
  if (first !== '{' && first !== '[') return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

/**
 * Check for a native bridge at call time (not import time) so the WS/WASM
 * bridges work.
 *
 * Tests `backend`, not just `window.__SONGBIRD__`: the object is also used as
 * a plain namespace for non-IPC hooks (`installRtFrameBinaryHook` mints it if
 * absent), so its mere presence does not mean anything can be invoked. Testing
 * the object alone made every `send()` on a bridge-less boot die with
 * `undefined is not an object (evaluating '...backend.emitEvent')` instead of
 * the honest rejection below.
 */
export const hasNativeBridge = (): boolean =>
  typeof window !== 'undefined' && !!window.__SONGBIRD__?.backend;

class PromiseHandler {
  private lastPromiseId = 0;
  private promises = new Map<number, { resolve: (value: unknown) => void; reject: (reason: unknown) => void }>();
  private listenerRegistered = false;

  private ensureListener(): void {
    if (this.listenerRegistered) return;
    if (!hasNativeBridge()) return;
    this.listenerRegistered = true;
    window.__SONGBIRD__!.backend.addEventListener(
      '__songbird__complete',
      ({ promiseId, result }: { promiseId: number; result: unknown }) => {
        const entry = this.promises.get(promiseId);
        if (entry) {
          // Tauri's WKWebView bridge serializes IPC replies as JSON
          // strings (e.g. \`result: "{\\"clipId\\":\\"xyz\\"}"\`) while the
          // headless WS transport returns objects directly. Normalize
          // here so every \`send()\` caller gets the parsed shape — no
          // scattered \`typeof res === 'string' ? JSON.parse : res\`
          // hacks at every call site.
          entry.resolve(maybeParseJson(result));
          this.promises.delete(promiseId);
        }
      },
    );
  }

  createPromise(): [number, Promise<unknown>] {
    this.ensureListener();
    const promiseId = this.lastPromiseId++;
    const result = new Promise<unknown>((resolve, reject) => {
      this.promises.set(promiseId, { resolve, reject });
    });
    return [promiseId, result];
  }
}

const promiseHandler = new PromiseHandler();

/**
 * Invoke a Rust command with named parameters.
 *
 * This is the low-level IPC entry point. It works in both native (Tauri)
 * and WebSocket modes because both wire up window.__SONGBIRD__ with the
 * same contract (backend.emitEvent → postMessage → Rust dispatcher).
 *
 * Channel handlers should NOT call this directly — they receive a `dispatch`
 * function from the Transport interface instead. Only bridge-level code
 * (stateBridge for Zustand persistence) should import this directly.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function invoke(name: string, params: Record<string, any> = {}): Promise<unknown> {
  if (!hasNativeBridge()) {
    return Promise.reject(new Error(`[invoke] No native bridge available for '${name}'`));
  }

  const [promiseId, result] = promiseHandler.createPromise();

  window.__SONGBIRD__!.backend.emitEvent('__songbird__invoke', {
    name,
    params,
    __named: true,
    resultId: promiseId,
  });

  return result;
}

/** Dispatch function type — passed to channel command builders */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Dispatch = (name: string, params: Record<string, any>) => Promise<unknown>;
